import React from "react";
import { Link, useInRouterContext } from 'react-router-dom';
import Markdown from "markdown-to-jsx";
import "./AssistantMarkdown.css";

export function safeResponseUrl(value, allowRelative = false) {
  if (typeof value !== "string" || /[\s<>"\\]/.test(value) ||
      Array.from(value).some(character => character.charCodeAt(0) < 32 || character.charCodeAt(0) === 127)) return null;
  if (allowRelative && (/^\/(?!\/)/.test(value) || value.startsWith("#"))) return value;
  try {
    const url = new URL(value);
    return ["http:", "https:"].includes(url.protocol) && !url.username && !url.password ? url.href : null;
  } catch { return null; }
}

export function citationSources(citations = []) {
  const sources = new Map();
  for (const citation of citations || []) {
    const url = safeResponseUrl(citation.url);
    if (url && !sources.has(url)) sources.set(url, {...citation, url, number: sources.size + 1});
  }
  return [...sources.values()];
}

function prepareCitations(content, citations) {
  // Provider offsets count Unicode code points; JS string indices count UTF-16 units.
  const characters = Array.from(content);
  const sources = new Map(citationSources(citations).map(source => [source.url, source]));
  const links = new Map();
  let prefix = "#timelyzer-citation-";
  while (content.includes(prefix)) prefix += "x";
  let position = 0;
  let markdown = "";
  for (const citation of [...(citations || [])].sort((a, b) => a.start - b.start)) {
    const source = sources.get(safeResponseUrl(citation.url));
    if (!source || !Number.isInteger(citation.start) || !Number.isInteger(citation.end) ||
        citation.start < position || citation.end > characters.length || citation.end <= citation.start) continue;
    const text = characters.slice(citation.start, citation.end).join("");
    const marker = /^(?:[【〖][\s\S]*[】〗]|cite[\s\S]*)$/.test(text);
    const reference = `${prefix}${links.size}`;
    links.set(reference, {...source, marker: marker || text.includes("\n")});
    const label = marker || text.includes("\n") ? String(source.number) : text.replace(/([\\[\]`*_~])/g, "\\$1");
    markdown += characters.slice(position, citation.start).join("") +
      (text.includes("\n") ? `${text} ` : "") + `[${label}](${reference})`;
    position = citation.end;
  }
  return {markdown: markdown + characters.slice(position).join(""), links};
}

function ScrollTable({children}) {
  return <div className="assistant-table-scroll" role="region" aria-label="Response table" tabIndex={0}>
    <table>{children}</table>
  </div>;
}

function CodeBlock({children}) {
  return <pre tabIndex={0} aria-label="Code block">{children}</pre>;
}

export default function AssistantMarkdown({content = "", citations = []}) {
  const inRouter = useInRouterContext();
  const {markdown, links} = prepareCitations(String(content || ""), citations);
  function ResponseLink({href, children, title}) {
    const source = links.get(href);
    const url = source?.url || safeResponseUrl(href, true);
    if (!url) return <span>{children}</span>;
    const external = /^https?:/.test(url);
    if (inRouter && url.startsWith('/')) return <Link to={url} title={title}>{children}</Link>;
    return <a href={url} target={external ? "_blank" : undefined} rel={external ? "noopener noreferrer" : undefined}
      title={source?.title || title} className={source?.marker ? "assistant-citation" : undefined}
      aria-label={source?.marker ? `Source ${source.number}: ${source.title || new URL(url).hostname}` : undefined}>
      {source?.marker ? source.number : children}
    </a>;
  }
  // Images remain explicit links: merely reading a response must not load remote pixels.
  function ResponseImage({src, alt}) {
    return <ResponseLink href={src}>{alt || "View image"}</ResponseLink>;
  }
  return <Markdown className="assistant-markdown" options={{
    forceBlock: true, forceWrapper: true, wrapper: "div", disableParsingRawHTML: true,
    enforceAtxHeadings: true,
    overrides: {
      a: ResponseLink, img: ResponseImage, table: ScrollTable, pre: CodeBlock,
      input: ({checked}) => <input type="checkbox" checked={!!checked} readOnly disabled />,
    },
  }}>{markdown}</Markdown>;
}
